import React, { useCallback, useEffect, useState } from "react";
import { RefreshControl, StyleSheet, View } from "react-native";
import { ScrollViewWithHeaders } from "@codeherence/react-native-header";

import AccountHeader from "../AccountHeader";

import EditAccountAttributes from "./EditProfileAttributes";
import AvatarPhotoGuidelineCard from "./AvatarPhotoGuidelineCard";
import BackgroundImage from "./AccountHeader/Components/BackgroundImage";
import AccountAvatar from "./AccountHeader/Components/AccountAvatar";

import { type AppUserSession } from "@/schemas/auth/session";
import { type UserProfile } from "@/schemas/user/profile";
import { Spacings } from "@/styling/spacings";
import { type UserImageMedia } from "@/schemas/user/image";

const LARGE_HEADER_DEFAULT_HEIGHT = 270;

type CollapsableAccountProps = {
  session: AppUserSession;
  profile: UserProfile;
  onRefresh: () => Promise<void>;
};

function CollapsableAccount({
  session,
  profile,
  onRefresh,
}: CollapsableAccountProps): React.JSX.Element {
  const [refreshing, setRefreshing] = useState(false);
  const [largeHeaderHeight, setLargeHeaderHeight] = useState(
    LARGE_HEADER_DEFAULT_HEIGHT,
  );
  const [background, setBackground] = useState<UserImageMedia | null>(
    profile.background,
  );

  const handleBackgroundChange = (image: UserImageMedia): void => {
    setBackground(image);
  };

  useEffect(() => {
    setBackground(profile.background);
  }, [profile.background]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh]);

  const LargeProfileHeaderComponent = useCallback(
    (): React.JSX.Element => (
      <View
        style={styles.largeProfileHeaderContainer}
        onLayout={(e) => {
          setLargeHeaderHeight(e.nativeEvent.layout.height);
        }}
      >
        <BackgroundImage
          session={session}
          onChangeBackground={handleBackgroundChange}
        />
        <AccountAvatar session={session} profile={profile} />
      </View>
    ),
    [session, profile],
  );

  return (
    <View style={styles.container}>
      <ScrollViewWithHeaders
        HeaderComponent={(props) => (
          <AccountHeader
            {...props}
            background={background}
            largeHeaderHeight={largeHeaderHeight}
          />
        )}
        bounces={false} // TODO: disable bounce for now to prevent header image from stretching down too much
        LargeHeaderComponent={LargeProfileHeaderComponent}
        scrollEventThrottle={16}
        scrollIndicatorInsets={{ top: largeHeaderHeight }}
        disableAutoFixScroll
        disableLargeHeaderFadeAnim
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            progressViewOffset={largeHeaderHeight}
          />
        }
      >
        <View style={styles.scrollViewContent}>
          <AvatarPhotoGuidelineCard />
          <EditAccountAttributes
            session={session}
            attributes={profile.attributes}
          />
        </View>
      </ScrollViewWithHeaders>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  largeProfileHeaderContainer: {
    flex: 1,
    zIndex: 2,
  },
  scrollViewContent: {
    flex: 1,
    marginTop: Spacings.s1,
  },
});

export default CollapsableAccount;
